import { inject, type Provider } from '@angular/core';
import { type Observable, catchError, finalize, map, of, shareReplay } from 'rxjs';
import {
  coincide,
  fuentePantallas,
  proveerFuenteIrA,
  type IrAResultado,
} from '@intelishs-group/ui/ir-a';
import { ApiRequestError } from '@shared/api';
import type { Alcance } from '@schemas/catalogo';
import type { FilaTrabajador } from '@schemas/trabajadores';
import { CatalogoApiService } from './catalogo-api.service';
import {
  CatalogosService,
  nombreCliente,
  nombreEmpresa,
  nombreGrupo,
  textoNomina,
  type Catalogos,
} from './catalogos.service';
import { EspacioService, rutaDeAlcance } from './espacio.service';
import { NavegacionService, RUTA_TRABAJADORES } from './navegacion.service';
import { MENSAJE_SIN_SERVICIO, SIN_SERVICIO } from './transporte-api.service';

const MAX_REGISTROS = 5;
const MAX_TRABAJADORES = 8;

/** Lo que la lista de trabajadores acepta como búsqueda (100 caracteres, 6 palabras); más no se pide. */
function buscable(t: string): boolean {
  return t.length >= 2 && t.length <= 100 && t.split(/\s+/).length <= 6;
}

/**
 * Las fuentes de «Ir a…» (Ctrl+K) del portal. Van en `app.config.ts`, en la raíz:
 * el buscador lo monta el marco (`<intelishs-shell-sesion>`, librerías ≥ 5.3.1),
 * por encima del `<router-outlet>`, y una fuente provista en una ruta no le llega.
 *
 * Hasta el 2026-10-23 esto era un componente propio (`components/ir-a`) con su
 * botón, su Ctrl+K y su ventana, montado en `[headerContent]`. Con la librería
 * en 5.3.x el marco trae los suyos, y los dos habrían escuchado el mismo atajo:
 * lo que era propio —qué se busca— vive aquí; lo demás es de la librería.
 *
 * - **Pantallas**: las de `NavegacionService.pantallas()`, con de qué área o
 *   registro cuelgan como contexto. Llevan el `tipo` de las del marco y las
 *   REEMPLAZAN en su lugar: el menú sólo trae las áreas, no sus pantallas.
 * - **Empresas, clientes y nóminas**: de la carga de catálogos de la sesión,
 *   sólo las que el usuario puede abrir (la misma regla que para entrar a un
 *   espacio). Con el módulo `empresas-clientes`.
 * - **Trabajadores**: en el servidor (`GET /trabajadores?texto=`), desde 2
 *   caracteres y 300 ms después de la última tecla. Con el módulo `personal`.
 */
export function proveerFuentesIrA(): Provider[] {
  return [
    proveerFuenteIrA(() => {
      const navegacion = inject(NavegacionService);
      return fuentePantallas(() => navegacion.pantallas());
    }),
    proveerFuenteIrA(() => {
      const { activa, conCatalogos, entra } = registros();
      return {
        tipo: 'empresa',
        titulo: 'Empresas',
        icono: 'bank',
        max: MAX_REGISTROS,
        activa,
        buscar: (_t, palabras) =>
          conCatalogos((c) =>
            c.empresas
              .filter(
                (e) =>
                  coincide(palabras, e.razon_social, e.rfc) && entra({ empresa_id: String(e.id) }),
              )
              .sort(
                (x, y) =>
                  Number(y.activa) - Number(x.activa) ||
                  x.razon_social.localeCompare(y.razon_social),
              )
              .map(
                (e): IrAResultado => ({
                  clave: String(e.id),
                  texto: e.razon_social,
                  meta: [e.rfc, e.activa ? '' : 'De baja'].filter((x) => x).join(' · '),
                  ruta: rutaDeAlcance({ empresa_id: String(e.id) }, 'datos'),
                }),
              ),
          ),
      };
    }),
    proveerFuenteIrA(() => {
      const { activa, conCatalogos, entra } = registros();
      return {
        tipo: 'cliente',
        titulo: 'Clientes',
        icono: 'team',
        max: MAX_REGISTROS,
        activa,
        buscar: (_t, palabras) =>
          conCatalogos((c) =>
            c.clientes
              .filter(
                (k) =>
                  coincide(palabras, k.nombre, nombreGrupo(c, k.grupo_id)) &&
                  entra({ cliente_id: String(k.id) }),
              )
              .sort(
                (x, y) => Number(y.activo) - Number(x.activo) || x.nombre.localeCompare(y.nombre),
              )
              .map((k): IrAResultado => {
                const grupo = nombreGrupo(c, k.grupo_id);
                return {
                  clave: String(k.id),
                  texto: k.nombre,
                  meta: [grupo ? `Grupo ${grupo}` : '', k.activo ? '' : 'De baja']
                    .filter((x) => x)
                    .join(' · '),
                  ruta: rutaDeAlcance({ cliente_id: String(k.id) }, 'datos'),
                };
              }),
          ),
      };
    }),
    proveerFuenteIrA(() => {
      const { activa, conCatalogos, entra, espacio } = registros();
      return {
        tipo: 'nomina',
        titulo: 'Nóminas',
        icono: 'schedule',
        max: MAX_REGISTROS,
        activa,
        buscar: (_t, palabras) =>
          conCatalogos((c) =>
            c.nominas
              .map((t) => ({
                t,
                // Las nóminas del cliente en la empresa (alta y cambio), en Empresas y clientes.
                alcance: { empresa_id: String(t.empresa_id), cliente_id: String(t.cliente_id) },
                cliente: nombreCliente(c, t.cliente_id),
                empresa: nombreEmpresa(c, t.empresa_id),
              }))
              .filter(
                (n) =>
                  coincide(palabras, n.t.clave, n.t.nombre, n.cliente, n.empresa) &&
                  entra({ ...n.alcance, tipo_nomina_id: String(n.t.id) }) &&
                  espacio.puedeVerSeccion(n.alcance, 'nominas'),
              )
              .sort(
                (x, y) =>
                  Number(y.t.activo) - Number(x.t.activo) || x.t.clave.localeCompare(y.t.clave),
              )
              .map(
                (n): IrAResultado => ({
                  clave: String(n.t.id),
                  texto: textoNomina(n.t),
                  meta: [n.cliente, n.empresa, n.t.activo ? '' : 'De baja']
                    .filter((x) => x)
                    .join(' · '),
                  ruta: rutaDeAlcance(n.alcance, 'nominas'),
                }),
              ),
          ),
      };
    }),
    proveerFuenteIrA(() => {
      const navegacion = inject(NavegacionService);
      const api = inject(CatalogoApiService);
      const catalogos = inject(CatalogosService);
      return {
        tipo: 'trabajador',
        titulo: 'Trabajadores',
        icono: 'idcard',
        minimo: 2,
        espera: 300,
        max: MAX_TRABAJADORES,
        activa: () => navegacion.tieneArea('personal'),
        buscar: (texto) =>
          buscable(texto)
            ? api
                .trabajadores({ texto, limite: MAX_TRABAJADORES })
                .pipe(
                  map((p) => p.elementos.map((t) => aResultadoTrabajador(t, catalogos.datos()))),
                )
            : [],
        // Mientras nominaapi no tenga `GET /trabajadores` (404/405 vacío = `SIN_SERVICIO`) se dice el
        // mensaje estándar, y lo demás sigue buscando.
        mensajeError: (e) =>
          e instanceof ApiRequestError && e.errors[0]?.code === SIN_SERVICIO
            ? `Trabajadores: ${MENSAJE_SIN_SERVICIO}`
            : null,
      };
    }),
  ];
}

/** Un trabajador como resultado: su nombre y, abajo, número, nómina y empresa. Lleva a su ficha. */
export function aResultadoTrabajador(t: FilaTrabajador, c: Catalogos | null): IrAResultado {
  return {
    clave: String(t.relacion_id),
    texto: t.nombre_completo,
    meta: [
      t.numero_empleado ? `No. ${t.numero_empleado}` : '',
      t.nomina ?? 'Sin asignar',
      c ? nombreEmpresa(c, t.empresa_id) : '',
    ]
      .filter((x) => x)
      .join(' · '),
    ruta: `${RUTA_TRABAJADORES}/${t.relacion_id}`,
  };
}

/** La carga en vuelo de los catálogos, compartida por las tres fuentes que los leen. */
const cargasEnVuelo = new WeakMap<CatalogosService, Observable<Catalogos | null>>();

/**
 * Lo que comparten empresas, clientes y nóminas: el módulo que las habilita, la
 * regla de alcance y los catálogos de la sesión.
 *
 * Si los catálogos todavía no llegaron —se abre Ctrl+K antes de entrar a una
 * pantalla que los pida— se piden UNA vez, tolerantes, aunque las tres fuentes
 * busquen a la vez. Si fallan, las tres salen vacías: lo mismo que hacía el
 * buscador local, que no tumbaba nada por ellos.
 */
function registros() {
  const navegacion = inject(NavegacionService);
  const catalogos = inject(CatalogosService);
  const espacio = inject(EspacioService);

  const cargados = (): Observable<Catalogos | null> => {
    if (catalogos.deLaSesion()) return of(catalogos.datos());
    let carga = cargasEnVuelo.get(catalogos);
    if (!carga) {
      carga = catalogos.cargar(true).pipe(
        map((): Catalogos | null => catalogos.datos()),
        catchError(() => of(null)),
        finalize(() => cargasEnVuelo.delete(catalogos)),
        shareReplay(1),
      );
      cargasEnVuelo.set(catalogos, carga);
    }
    return carga;
  };

  return {
    espacio,
    activa: () => navegacion.tieneArea('empresas-clientes'),
    /** La misma regla que para entrar a un espacio (`EspacioService.puedeEntrar`). */
    entra: (a: Alcance) => espacio.puedeEntrar(a),
    conCatalogos: (armar: (c: Catalogos) => IrAResultado[]) =>
      cargados().pipe(map((c) => (c ? armar(c) : []))),
  };
}
